import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Displays the 2d-SVG specified by url2d.
 * Also displays width and height dimensional markers.
 * Also rotates the SVG according to rotation (in degrees).
 * @param {*} param0 
 * @returns 
 */
const SVGElement = ({ url2d, toggleOption, setIs2dLoading, rotation, is2dLoading }) => {
  const svgContainerRef = useRef(null);
  const [bounds, setBounds] = useState(null);

  /**
   * Either clears the SVG and bounds, or loads the SVG from the specified URL.
   */
  useEffect(() => {
    if (toggleOption !== '2d') {
      // changing the toggle to something else than 2d should not trigger fetch of SVG
      return;
    }
    if (!url2d) {
      // clear the svgElement, because none is set
      if (svgContainerRef.current) {
        svgContainerRef.current.innerHTML = '';
      }
      // clear the bounds
      setBounds(null);
      return;
    }
    // fetch the 2d-svg from the specified url
    axios
      .get(url2d)
      .then(response => {
        if (svgContainerRef.current) {
          svgContainerRef.current.innerHTML = "";
          svgContainerRef.current.innerHTML = response.data;
          setIs2dLoading(false);
          const svgElement = svgContainerRef.current.querySelector("svg");
          if (svgElement) {
            // rotate svg, set width and height to 100%
            svgElement.style.transform = `rotate(${rotation}deg)`;
            svgElement.style.width = '100%';
            svgElement.style.height = '100%';
            // make SVG non-clickable (there are some svg that contain an a-element)
            svgElement.style.pointerEvents = "none";
            updateMarkerBounds();
            // add event listener to update marker-bounds when resizing
            window.addEventListener("resize", updateMarkerBounds);
          }
        }
      })
      .catch(error => {
        console.log(error);
      });
    // remove event listener again
    return () => window.removeEventListener("resize", updateMarkerBounds);
  }, [url2d, toggleOption, setIs2dLoading]);

  /**
   * Updates the marker-bounds in the next requested animation-frame.
   * @returns 
   */
  const updateMarkerBounds = () => {
    const svgElement = svgContainerRef.current.querySelector("svg");
    if (!svgElement) return;
    requestAnimationFrame(() => {
      const bbox = svgElement.getBoundingClientRect();
      let width = bbox.width;
      let height = bbox.height;
      if (rotation === 90 || rotation === 270) {
        width = bbox.height;
        height = bbox.width;
      }
      setBounds({ width, height });
    });
  };

  /**
   * Updates the rotation of the 2d-SVG.
   */
  useEffect(() => {
    if (svgContainerRef.current) {
      const svgElement = svgContainerRef.current.querySelector("svg");
      if (svgElement) {
        svgElement.style.transform = `rotate(${rotation}deg)`;
        updateMarkerBounds(svgElement);
      }
    }
  }, [rotation]);

  /**
   * A horizontal marker to show horizontal width.
   * @param {*} param0 
   * @returns 
   */
  const MarkerHorizontal = ({ width }) => {
    return <div style={{
      display: 'flex',
      alignItems: 'center',
      position: 'absolute',
      width: width,
      height: '10px',
      top: 'calc(100% + 1px)',
      left: '50%',
      transform: 'translateX(-50%)'
    }} >
      <div style={{
        width: '2px',
        height: '10px',
        backgroundColor: 'black'
      }} ></div>
      <div style={{
        flexGrow: 1,
        height: '2px',
        backgroundColor: 'black',
        position: 'relative'
      }} ></div>
      <div style={{
        width: '2px',
        height: '10px',
        backgroundColor: 'black'
      }} ></div>
    </div>
  };

  /**
   * A vertical marker to show vertical height.
   * @param {*} param0 
   * @returns 
   */
  const MarkerVertical = ({ height }) => {
    return <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      width: '10px',
      height: height,
      top: '50%',
      right: '-4px',
      transform: 'translate(100%, -50%)'
    }} >
      <div style={{
        width: '10px',
        height: '2px',
        backgroundColor: 'black'
      }} ></div>
      <div style={{
        flexGrow: 1,
        width: '2px',
        backgroundColor: 'black',
        position: 'relative'
      }} ></div>
      <div style={{
        width: '10px',
        height: '2px',
        backgroundColor: 'black'
      }} ></div>
    </div>
  };

  /**
   * The width and height dimensional-markers of the 2d-SVG.
   * Depending on the rotation, the horizontal and vertical marker
   * use either the width or height of the bounding box as length.
   */
  const dimensionalMarkers = useMemo(() => {
    if (!bounds) return;
    switch (rotation) {
      case 90:
      case 270:
        return [<MarkerHorizontal width={bounds.height} />, <MarkerVertical height={bounds.width} />];
      case 0:
      case 180:
      default:
        return [<MarkerHorizontal width={bounds.width} />, <MarkerVertical height={bounds.height} />];
    }
  }, [rotation, bounds]);

  return <div style={{
    position: 'relative',
    maxHeight: '95%',
    height: '95%',
    width: '70%',
    display: is2dLoading ? 'none' : 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column'
  }}>
    <div style={{
      width: '100%',
      maxHeight: '100%',
      flexGrow: '1',
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column'
    }}>
      <div style={{
        height: 'fit-content',
        maxHeight: '100%',
        maxWidth: '40%',
        width: 'fit-content',
        position: 'relative',
      }}
        ref={svgContainerRef}>
      </div>
    </div>
    {dimensionalMarkers}
  </div>
};

export default SVGElement;